'use client';

import Particles from '@/components/Particles';
import { Button } from '@/components/ui/button';
import {
  ArrowUpRight,
  Database,
  FileText,
  Languages,
  Sparkles,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const features = [
  {
    icon: FileText,
    label: '格式完美保留',
    color: 'blue',
  },
  {
    icon: Sparkles,
    label: 'AI 大模型智能翻译',
    color: 'purple',
  },
  {
    icon: Languages,
    label: '12+ 国语言互译',
    color: 'green',
  },
  {
    icon: Database,
    label: '支持 100MB 大文件',
    color: 'orange',
  },
];

const colorMap = {
  blue: {
    bg: 'bg-blue-500/10 dark:bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-500/30 dark:border-blue-500/20',
    glow: 'group-hover:shadow-blue-500/10 dark:group-hover:shadow-blue-500/20',
  },
  purple: {
    bg: 'bg-purple-500/10 dark:bg-purple-500/10',
    text: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-500/30 dark:border-purple-500/20',
    glow: 'group-hover:shadow-purple-500/10 dark:group-hover:shadow-purple-500/20',
  },
  green: {
    bg: 'bg-green-500/10 dark:bg-green-500/10',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-500/30 dark:border-green-500/20',
    glow: 'group-hover:shadow-green-500/10 dark:group-hover:shadow-green-500/20',
  },
  orange: {
    bg: 'bg-orange-500/10 dark:bg-orange-500/10',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-500/30 dark:border-orange-500/20',
    glow: 'group-hover:shadow-orange-500/10 dark:group-hover:shadow-orange-500/20',
  },
};

export function HeroSection() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setMounted(true), 0);
    return () => window.clearTimeout(timer);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  const particleColors = isDark
    ? ['#3b82f6', '#8b5cf6', '#ffffff']
    : ['#3b82f6', '#8b5cf6', '#94a3b8'];

  return (
    <section className="relative flex flex-col items-center justify-center overflow-hidden px-4 py-24 md:py-32 lg:py-40 bg-neutral-50 dark:bg-neutral-950 transition-colors duration-500">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-linear-to-b from-white via-neutral-50 to-neutral-100 dark:from-neutral-950 dark:via-neutral-950 dark:to-neutral-900 transition-colors duration-500" />
        <div className="absolute inset-0 opacity-40 dark:opacity-30 bg-[radial-gradient(#94a3b8_1px,transparent_1px)] dark:bg-[radial-gradient(#fff_1px,transparent_1px)] bg-size-[24px_24px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gradient-radial from-blue-400/5 via-purple-400/3 to-transparent dark:from-blue-500/10 dark:via-purple-500/5 dark:to-transparent rounded-full blur-3xl transition-opacity duration-500" />
        <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-neutral-300 to-transparent dark:via-white/20 transition-colors duration-500" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-neutral-200 to-transparent dark:via-white/10 transition-colors duration-500" />
      </div>

      <div className="absolute inset-0 opacity-25 dark:opacity-40 transition-opacity duration-500">
        <Particles
          particleColors={particleColors}
          particleCount={150}
          particleSpread={12}
          speed={0.15}
          particleBaseSize={60}
          moveParticlesOnHover={true}
          alphaParticles={true}
          disableRotation={true}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center max-w-5xl mx-auto space-y-10">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-200 bg-white/80 dark:border-white/10 dark:bg-white/5 backdrop-blur-xs text-sm text-neutral-600 dark:text-neutral-300 shadow-xs dark:shadow-none transition-all duration-300">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
          注册即送 5 万字免费翻译额度
        </div>

        <div className="relative">
          <h1 className="absolute inset-0 text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-balance text-blue-500/30 blur-2xl select-none pointer-events-none pb-4 opacity-0 dark:opacity-100 transition-opacity duration-500">
            超能文献 · AI文档翻译
            <br />
            翻译有温度 | 专业不走样
          </h1>
          <h1 className="relative text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-balance bg-linear-to-b from-neutral-900 via-neutral-800 to-neutral-500 dark:from-white dark:via-white dark:to-white/60 bg-clip-text text-transparent pb-4 transition-all duration-500">
            超能文献 · AI文档翻译
            <br />
            翻译有温度 | 专业不走样
          </h1>
        </div>

        <p className="max-w-2xl text-base md:text-lg text-neutral-600 dark:text-neutral-400 text-balance leading-relaxed transition-colors duration-300">
          AI
          智能翻译平台，支持12+语言互译，PDF/Word/PPT/Excel/html/epub等多种格式完美还原，基于一线主流大模型深度优化，支持
          100MB 超大文件。注册即送5万字免费翻译额度。
        </p>

        <div className="flex flex-col sm:flex-row gap-4 items-center pt-2">
          <Button
            asChild
            size="lg"
            className="relative h-12 rounded-full px-8 bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-100 font-semibold text-base border-0 shadow-lg shadow-neutral-900/20 hover:shadow-neutral-900/30 dark:shadow-white/20 dark:hover:shadow-white/30 transition-all duration-300"
          >
            <Link href="/translate/upload">
              开始翻译
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="h-12 rounded-full px-8 border-neutral-300 bg-white/50 text-neutral-700 hover:text-neutral-900 hover:bg-white hover:border-neutral-400 dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:text-white dark:hover:bg-white/10 dark:hover:border-white/30 font-medium text-base backdrop-blur-xs transition-all duration-300"
          >
            <Link href="/pricing">查看优惠</Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 pt-12 w-full max-w-4xl">
          {features.map((feature) => {
            const colors = colorMap[feature.color as keyof typeof colorMap];
            return (
              <div
                key={feature.label}
                className={`group relative flex flex-col items-center gap-3 p-4 md:p-5 rounded-2xl border border-neutral-200 bg-white/80 dark:border-white/10 dark:bg-white/3 backdrop-blur-xs hover:border-neutral-300 hover:bg-white dark:hover:border-white/20 dark:hover:bg-white/6 transition-all duration-300 cursor-default ${colors.glow} hover:shadow-lg shadow-xs dark:shadow-none`}
              >
                <div className="absolute inset-0 rounded-2xl bg-linear-to-br from-neutral-100/50 to-transparent dark:from-white/2 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div
                  className={`relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl ${colors.bg} ${colors.text} transition-transform duration-300 group-hover:scale-110`}
                >
                  <feature.icon className="h-5 w-5 md:h-6 md:w-6" />
                </div>
                <span className="relative text-sm text-neutral-600 dark:text-neutral-300 font-medium text-center group-hover:text-neutral-900 dark:group-hover:text-white transition-colors duration-300">
                  {feature.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}
